import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import InputFieldFloat from "../../inputs/InputFieldFloat";
import InputTextAreaFloat from "../../inputs/InputTextAreaFloat";
import CustomSelectFloat from "../../inputs/CustomSelectFloat";
import { useGetLocationDataQuery } from "../../../services/public/location";
import { COUNTRY_OPTIONS, TIMEZONES, COUNTRY_TIMEZONE_MAP } from "../../../constants/landingPage";

const CompanyDetailsStep = () => {
  const { t } = useTranslation("signup");
  const { values, errors, touched, handleChange, setFieldValue } = useFormikContext();
  const { data: locationData } = useGetLocationDataQuery();

  const countryOptions = COUNTRY_OPTIONS.map(c => ({
    label: c.name,
    value: c.label.toLowerCase(),
    iso: c.label.toLowerCase()
  }));

  const timezoneOptions = TIMEZONES.map(tz => ({
    label: t(`timezones.${tz}`),
    value: tz
  }));

  useEffect(() => {
    if (!locationData) return;

    if (!values.country && locationData.country) {
      const detectedCode = locationData.country.toLowerCase();
      const countryInOptions = COUNTRY_OPTIONS.find(c => c.label.toLowerCase() === detectedCode);
      if (countryInOptions) {
        setFieldValue('country', countryInOptions.label);
      }
    }

    // Timezone auto-detect
    if (!values.timezone && locationData.country) {
      const countryCode = locationData.country.toUpperCase();
      const mappedTimezone = COUNTRY_TIMEZONE_MAP[countryCode];
      // Only set if the mapped timezone exists in our TIMEZONES list
      if (mappedTimezone && TIMEZONES.includes(mappedTimezone)) {
        setFieldValue('timezone', mappedTimezone);
      }
    }
  }, [locationData, setFieldValue, values.country, values.timezone]);

  return (
    <div className="field-row i-mb-8">
      <div className="field-block grid-full">
        <InputFieldFloat
          label={t("company_name_label")}
          floating={true}
          name="companyName"
          value={values.companyName}
          onChange={handleChange}
          error={errors.companyName}
          touched={touched.companyName}
          required={true}
        />
      </div>

      <div className="field-block grid-full">
        <InputFieldFloat
          label={t("address")}
          floating={true}
          name="address"
          value={values.address}
          onChange={handleChange}
          error={errors.address}
          touched={touched.address}
          required={true}
        />
      </div>
      
      <div className="field-block">
        <InputFieldFloat
          label={t("city")}
          floating={true}
          name="city"
          value={values.city}
          onChange={handleChange}
          error={errors.city}
          touched={touched.city}
          required={true}
        />
      </div>

      <CustomSelectFloat
        label={t("country")}
        key={values.country}
        value={values.country}
        options={countryOptions}
        onChange={(val) => setFieldValue('country', val)}
        error={errors.country}
        touched={touched.country}
        required={true}
        scrollHide={true}
        renderOption={(opt) => (
          <>
            <img 
              src={`https://hatscripts.github.io/circle-flags/flags/${opt.iso}.svg`} 
              alt={opt.label} 
              width="20" 
              height="20" 
            />
            {opt.label}
          </>
        )}
        renderSelected={(opt) => (
          <>
            <img 
              src={`https://hatscripts.github.io/circle-flags/flags/${opt.iso}.svg`} 
              alt={opt.label} 
              width="16" 
              height="16" 
            />
            {opt.label}
          </>
        )}
      />
      
      <CustomSelectFloat
        label={t("timezone")}
        value={values.timezone}
        options={timezoneOptions}
        onChange={(val) => setFieldValue('timezone', val)}
        error={errors.timezone}
        touched={touched.timezone}
        required={true}
        className="grid-full"
      />
      
      <div className="field-block grid-full">
        <InputTextAreaFloat
          label={t("about_company")}
          floating={true}
          placeholder={t("about_company_placeholder")}
          rows={5}
          name="about"
          value={values.about}
          onChange={handleChange}
          error={errors.about}
          touched={touched.about}
        />
      </div>
    </div>
  );
};

export default CompanyDetailsStep;
